import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { statusAPI, fileAPI, userAPI } from '../utils/api';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { Plus, X, Eye, Trash2, ChevronLeft, ChevronRight, Image as ImageIcon } from 'lucide-react';
import { track } from '../utils/firebase';

const STATUS_COLORS = ['#4338CA', '#DC2626', '#059669', '#D97706', '#7C3AED', '#0F172A', '#EC4899'];

const StatusView = () => {
  const { user } = useAuth();
  const [statusData, setStatusData] = useState({ my_statuses: null, others: [] });
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showViewer, setShowViewer] = useState(null);
  const [viewerIndex, setViewerIndex] = useState(0);
  const [newContent, setNewContent] = useState('');
  const [selectedColor, setSelectedColor] = useState('#4338CA');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    loadStatuses();
    const interval = setInterval(loadStatuses, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadStatuses = async () => {
    try {
      const res = await statusAPI.getStatuses();
      setStatusData(res.data);
    } catch (err) {
      console.error('Failed to load statuses', err);
    }
  };

  const handleCreateTextStatus = async () => {
    if (!newContent.trim()) return;
    try {
      await statusAPI.createStatus({ content: newContent, background_color: selectedColor });
      toast.success('Status posted');
      track.postStatus(file?.type?.startsWith('video') ? 'video' : 'image');
      setNewContent('');
      setShowCreateDialog(false);
      loadStatuses();
    } catch (err) {
      toast.error('Failed to post status');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const uploadRes = await fileAPI.uploadFile(file);
      await statusAPI.createStatus({ file_id: uploadRes.data.file_id, content: 'Photo status' });
      toast.success('Photo status posted');
      setShowCreateDialog(false);
      loadStatuses();
    } catch (err) {
      toast.error('Failed to upload status');
    } finally {
      setUploading(false);
    }
  };

  const openViewer = async (userStatuses, idx = 0) => {
    setShowViewer(userStatuses);
    setViewerIndex(idx);
    if (userStatuses.statuses[idx]) {
      try {
        await statusAPI.viewStatus(userStatuses.statuses[idx].id);
      } catch {}
    }
  };

  const nextStatus = async () => {
    if (!showViewer) return;
    const next = viewerIndex + 1;
    if (next < showViewer.statuses.length) {
      setViewerIndex(next);
      try {
        await statusAPI.viewStatus(showViewer.statuses[next].id);
      } catch {}
    } else {
      setShowViewer(null);
    }
  };

  const prevStatus = () => {
    if (viewerIndex > 0) setViewerIndex(viewerIndex - 1);
  };

  const handleDeleteStatus = async (statusId) => {
    try {
      await statusAPI.deleteStatus(statusId);
      toast.success('Status deleted');
      setShowViewer(null);
      loadStatuses();
    } catch {
      toast.error('Failed to delete');
    }
  };

  // Auto-advance after 5 seconds
  useEffect(() => {
    if (!showViewer) return;
    const timer = setTimeout(() => nextStatus(), 5000);
    return () => clearTimeout(timer);
  }, [showViewer, viewerIndex]);

  const currentStatus = showViewer?.statuses?.[viewerIndex];

  return (
    <div className="p-3" data-testid="status-view">
      <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
        {/* Add Status */}
        <div className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer" onClick={() => setShowCreateDialog(true)} data-testid="add-status-button">
          <div className="relative">
            <Avatar className="h-14 w-14 border-2 border-dashed border-[#4338CA]">
              <AvatarImage src={userAPI.getAvatarUrl(user?.avatar_url)} />
              <AvatarFallback className="bg-[#4338CA]/10 text-[#4338CA]">
                <Plus className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>
          </div>
          <span className="text-xs text-[#475569] truncate w-16 text-center">My Status</span>
        </div>

        {/* My Statuses */}
        {statusData.my_statuses && statusData.my_statuses.statuses.length > 0 && (
          <div
            className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
            onClick={() => openViewer(statusData.my_statuses)}
            data-testid="my-status-ring"
          >
            <div className="p-[3px] rounded-full bg-gradient-to-tr from-[#4338CA] to-[#7C3AED]">
              <Avatar className="h-14 w-14 border-2 border-white">
                <AvatarImage src={userAPI.getAvatarUrl(user?.avatar_url)} />
                <AvatarFallback className="bg-[#4338CA] text-white">{user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
            </div>
            <span className="text-xs text-[#475569] truncate w-16 text-center">You</span>
          </div>
        )}

        {/* Others' Statuses */}
        {statusData.others.map((item) => {
          const hasUnviewed = item.statuses.some(s => !s.viewed_by.includes(user?.id));
          return (
            <div
              key={item.user?.id}
              className="flex flex-col items-center gap-1 flex-shrink-0 cursor-pointer"
              onClick={() => openViewer(item)}
              data-testid={`status-ring-${item.user?.id}`}
            >
              <div className={`p-[3px] rounded-full ${hasUnviewed ? 'bg-gradient-to-tr from-[#4338CA] to-[#7C3AED]' : 'bg-[#94A3B8]'}`}>
                <Avatar className="h-14 w-14 border-2 border-white">
                  <AvatarImage src={userAPI.getAvatarUrl(item.user?.avatar_url)} />
                  <AvatarFallback className="bg-[#4338CA] text-white">{item.user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
              </div>
              <span className="text-xs text-[#475569] truncate w-16 text-center">{item.user?.name?.split(' ')[0]}</span>
            </div>
          );
        })}
      </div>

      {/* Create Status Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md" data-testid="create-status-dialog">
          <DialogHeader>
            <DialogTitle>Create Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div
              className="rounded-lg p-8 flex items-center justify-center min-h-[200px]"
              style={{ backgroundColor: selectedColor }}
            >
              <textarea
                value={newContent}
                onChange={(e) => setNewContent(e.target.value)}
                placeholder="Type your status..."
                className="bg-transparent text-white text-center text-lg font-medium placeholder:text-white/60 border-none outline-none resize-none w-full"
                rows={3}
                data-testid="status-text-input"
              />
            </div>
            <div className="flex gap-2 justify-center">
              {STATUS_COLORS.map((c) => (
                <button
                  key={c}
                  className={`w-8 h-8 rounded-full border-2 ${selectedColor === c ? 'border-[#0F172A] scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                  onClick={() => setSelectedColor(c)}
                  data-testid={`color-${c}`}
                />
              ))}
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCreateTextStatus} className="flex-1 bg-[#4338CA] hover:bg-[#3730A3]" disabled={!newContent.trim()} data-testid="post-status-button">
                Post Status
              </Button>
              <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
              <Button variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-status-image-button">
                <ImageIcon className="w-4 h-4 mr-2" />Photo
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Viewer */}
      {showViewer && currentStatus && (
        <div className="fixed inset-0 bg-black z-[100] flex flex-col" data-testid="status-viewer">
          {/* Progress bars */}
          <div className="flex gap-1 p-3">
            {showViewer.statuses.map((st, i) => (
              <div key={st.id || `progress-${i}`} className="flex-1 h-1 rounded-full bg-white/30 overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${i < viewerIndex ? 'bg-white w-full' : i === viewerIndex ? 'bg-white animate-progress' : 'w-0'}`}
                  style={i === viewerIndex ? { animation: 'progress 5s linear forwards' } : {}}
                />
              </div>
            ))}
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={userAPI.getAvatarUrl(showViewer.user?.avatar_url)} />
                <AvatarFallback className="bg-[#4338CA] text-white">{showViewer.user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
              <div>
                <p className="text-white font-medium text-sm">{showViewer.user?.name}</p>
                <p className="text-white/60 text-xs">
                  {new Date(currentStatus.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentStatus.user_id === user?.id && (
                <>
                  <div className="flex items-center gap-1 text-white/60 text-xs">
                    <Eye className="w-4 h-4" />
                    {currentStatus.viewed_by?.length || 0}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:text-red-400"
                    onClick={() => handleDeleteStatus(currentStatus.id)}
                    data-testid="delete-status-button"
                  >
                    <Trash2 className="w-5 h-5" />
                  </Button>
                </>
              )}
              <Button variant="ghost" size="icon" className="text-white" onClick={() => setShowViewer(null)} data-testid="close-status-viewer">
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 flex items-center justify-center relative" onClick={nextStatus}>
            {viewerIndex > 0 && (
              <button className="absolute left-2 z-10 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); prevStatus(); }}>
                <ChevronLeft className="w-8 h-8" />
              </button>
            )}
            {currentStatus.file_id ? (
              <img
                src={fileAPI.getFileUrl(currentStatus.file_id)}
                alt="Status"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <div
                className="w-full h-full flex items-center justify-center p-8"
                style={{ backgroundColor: currentStatus.background_color || '#4338CA' }}
              >
                <p className="text-white text-2xl font-medium text-center">{currentStatus.content}</p>
              </div>
            )}
            {viewerIndex < showViewer.statuses.length - 1 && (
              <button className="absolute right-2 z-10 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); nextStatus(); }}>
                <ChevronRight className="w-8 h-8" />
              </button>
            )}
          </div>
        </div>
      )}

      <style>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
};

export default StatusView;
